import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "streetAddress",
    "streetAddress2",
    "city",
    "state",
    "zipCode",
    "timeZone",
  ];

  static values = { apiKey: String };

  connect() {
    this.initWhenReady();
  }

  initWhenReady() {
    if (typeof google !== "undefined" && google.maps && google.maps.places) {
      this.initAutocomplete();
    } else {
      this.retryCount = (this.retryCount || 0) + 1;
      if (this.retryCount < 50) {
        this.retryTimer = setTimeout(() => this.initWhenReady(), 100);
      }
    }
  }

  initAutocomplete() {
    this.autocomplete = new google.maps.places.Autocomplete(
      this.streetAddressTarget,
      {
        types: ["address"],
        componentRestrictions: { country: "us" },
        fields: ["address_components", "geometry"],
      },
    );

    this.autocomplete.addListener("place_changed", () => {
      this.fillAddress();
    });
  }

  fillAddress() {
    const place = this.autocomplete.getPlace();
    if (!place.address_components) return;

    let streetNumber = "";
    let route = "";
    let subpremise = "";

    for (const component of place.address_components) {
      const type = component.types[0];

      switch (type) {
        case "street_number":
          streetNumber = component.long_name;
          break;
        case "route":
          route = component.long_name;
          break;
        case "subpremise":
          subpremise = component.long_name;
          break;
        case "locality":
          this.cityTarget.value = component.long_name;
          break;
        case "administrative_area_level_1":
          this.stateTarget.value = component.short_name;
          break;
        case "postal_code":
          this.zipCodeTarget.value = component.long_name;
          break;
      }
    }

    this.streetAddressTarget.value = `${streetNumber} ${route}`.trim();
    this.streetAddress2Target.value = subpremise;

    if (place.geometry && this.hasTimeZoneTarget) {
      this.detectTimeZone(place.geometry.location);
    } else {
      this.streetAddress2Target.focus();
    }
  }

  async detectTimeZone(location) {
    const lat = location.lat();
    const lng = location.lng();
    const timestamp = Math.floor(Date.now() / 1000);

    if (!this.apiKeyValue) return;

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${this.apiKeyValue}`,
      );
      const data = await response.json();

      if (data.status === "OK" && data.timeZoneId) {
        this.setTimeZone(data.timeZoneId);
      }
    } catch {
      // Silently fail — user can still select timezone manually
    }

    this.streetAddress2Target.focus();
  }

  setTimeZone(ianaName) {
    const select = this.timeZoneTarget;

    for (const option of select.options) {
      if (option.dataset.iana === ianaName || option.value === ianaName) {
        select.value = option.value;
        return;
      }
    }
  }

  disconnect() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  }
}
